import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {Outpost} from "../../../domain/outposts/outpost";
import {OutpostRepository} from "../../../domain/outposts/outpost-repository";
import {OutpostStatus} from "../../../domain/outposts/OutpostStatus";
import {interval, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: "evrideo-outpost-sidebar",
  templateUrl: "./outpost-sidebar.component.html",
  styleUrls: ["./outpost-sidebar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutpostSidebarComponent implements OnInit, OnDestroy {
  @Input()
  activeItemId: string | null = null;

  @Output()
  itemClick = new EventEmitter<{ outpostId: string; isOnline: boolean }>();

  items: Outpost[] = [];
  outpostStatuses: Map<number, OutpostStatus> = new Map();

  private readonly STATUS_UPDATE_INTERVAL_MS = 60000;
  private destroy$ = new Subject<void>();

  constructor(private outpostRepository: OutpostRepository,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  async ngOnInit(): Promise<void> {
    await this.loadOutposts();
    await this.loadStatuses();

    interval(this.STATUS_UPDATE_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadStatuses();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadOutposts(): Promise<void> {
    try {
      const outposts = await this.outpostRepository.getOutposts();
      this.items = outposts || [];
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      console.error("Error loading outposts:", error);
      this.items = [];
      this.changeDetectorRef.markForCheck();
    }
  }

  async loadStatuses(): Promise<void> {
    try {
      const statuses = await this.outpostRepository.getStatuses();
      this.outpostStatuses.clear();
      statuses.forEach((status) => {
        this.outpostStatuses.set(status.id, status);
      });
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      console.error("Error loading outpost statuses:", error);
      this.outpostStatuses.clear();
      this.changeDetectorRef.markForCheck();
    }
  }

  getOutpostStatus(outpost: Outpost): OutpostStatus | null {
    return this.outpostStatuses.get(outpost.id) || null;
  }

  isOutpostOnline(outpost: Outpost): boolean {
    const status = this.getOutpostStatus(outpost);
    return status ? status.isOnline : false;
  }

  onItemClick(item: Outpost): void {
    this.itemClick.emit({
      outpostId: item.id.toString(),
      isOnline: this.isOutpostOnline(item),
    });
  }

  isActive(item: Outpost): boolean {
    return this.activeItemId === item.id.toString();
  }
}
